import { GridReadyEvent } from 'ag-grid-community'
import { ReactElement, useCallback, useEffect, useMemo, useRef } from 'react'
import { useUpdatedSortMetricsAccess } from './useUpdatedSortMetricsAccess'
import { Run } from '../../utils/api'
import { getCellTypeColumnDefs } from './metadata'
import AgGridRunDetailsTable from '../shared/AgGridRunDetailsTable'
import useResetGridColumnState from './useResetGridColumnState'

interface ICellTypeGridProps {
  run: Run
}

const CellTypeGrid = ({ run }: ICellTypeGridProps): ReactElement => {
  const rowData = run.cell_counts ?? []
  const model = run.model ?? ''

  // IMPORTANT: This hook reads from feature flags, which may not be loaded immediately
  // On initial render, this often returns false, then updates to true once flags load
  const updatedMetricsEnabled = useUpdatedSortMetricsAccess()

  // Store grid API reference so we can update columnDefs after initial render
  const gridApiRef = useRef<GridReadyEvent['api'] | null>(null)

  // Column definitions depend on updatedMetricsEnabled flag
  // When the flag changes, useMemo recomputes the columnDefs with different cell renderers
  const columnDefs = useMemo(
    () =>
      getCellTypeColumnDefs({
        runId: run.run_id,
        total_cell_count: run.imaged_cell_count ?? run.total_cell_count,
        // Drives the tooltip wording: only imaged_cell_count has beads removed
        excludesBeads: run.imaged_cell_count !== undefined,
        model,
        updatedMetricsEnabled,
      }),
    [run.run_id, run.imaged_cell_count, run.total_cell_count, model, updatedMetricsEnabled]
  )

  const resetGridColumnState = useResetGridColumnState(columnDefs)

  const onGridReady = useCallback(
    (params: GridReadyEvent) => {
      // Save API reference for later use in useEffect
      gridApiRef.current = params.api
      resetGridColumnState(params)
    },
    [resetGridColumnState]
  )

  /**
   * CRITICAL: Handle columnDefs updates after initial render
   *
   * Problem:
   * - Component mounts with updatedMetricsEnabled=false (feature flags not loaded yet)
   * - Grid initializes with old column definitions (no ProgressCellComponent)
   * - Feature flags load, updatedMetricsEnabled becomes true
   * - useMemo recomputes columnDefs with new renderers
   * - BUT ag-grid v25 doesn't automatically update to use new renderers!
   *
   * Solution:
   * - Watch for columnDefs changes in useEffect
   * - Call api.setColumnDefs() to update column configuration
   * - Call api.refreshCells({ force: true }) to force re-render with new renderers
   *
   * Without refreshCells({ force: true }), ag-grid v25 caches the old cell renderer
   * and continues using it even after setColumnDefs is called.
   */
  useEffect(() => {
    if (gridApiRef.current) {
      gridApiRef.current.setColumnDefs(columnDefs)
      // Force ag-grid to re-render all cells with the new cell renderers
      // This is essential for ag-grid v25 to pick up cellRendererFramework changes
      gridApiRef.current.refreshCells({ force: true })
    }
  }, [columnDefs])

  return (
    <AgGridRunDetailsTable
      testId="cell-type-grid"
      customClass="context-menu-ag-grid"
      columnDefs={columnDefs}
      rowData={rowData}
      onGridReady={onGridReady}
    />
  )
}

export default CellTypeGrid
